/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */
(function () {
  /**
 * BulbGoogleClassroomImportService import courses from google classroom and open dialog to show course list.
 */
  angular.module('bulbApp.groupModule')
    .constant('CLASSROOM', {
      COURSES_URL: 'https://classroom.googleapis.com/v1/courses?teacherId=me&courseStates=ACTIVE&pageToken=',
      COURSE_BY_ID_URL: 'https://classroom.googleapis.com/v1/courses/',
      USER_INFO_URL: 'https://www.googleapis.com/oauth2/v1/userinfo',
    })
    .service('BulbGoogleClassroomImportService', ['$q', '$translate', 'BulbBusyService', 'BulbErrorService', 'BulbGroupService',
      'BulbModalService', 'CLASSROOM', 'CONFIG', 'google', 'Restangular', BulbGoogleClassroomImportService]);

  function BulbGoogleClassroomImportService(
    $q,
    $translate,
    BulbBusyService,
    BulbErrorService,
    BulbGroupService,
    BulbModalService,
    CLASSROOM,
    CONFIG,
    google,
    Restangular,
  ) {
    const that = this;

    /**
   * List of scope to get token after login with google.
   * https://developers.google.com/classroom/guides/auth#OAuth2Authorizing
   * @type {string[]|SCOPES}
   */
    const SCOPES = ['email', 'https://www.googleapis.com/auth/classroom.courses.readonly',
      'https://www.googleapis.com/auth/classroom.rosters.readonly',
      'https://www.googleapis.com/auth/classroom.profile.emails'];

    /**
   * List of api will be fetched .
   * @type {string[]}
   */
    const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/classroom/v1/rest'];

    /**
   * Store authentication token after authorize and send it to api which be fetched data from classroom.
   * @type {string}
   */
    this.oauthToken = null;

    /**
   * List of class which will be fetched from google classroom .
   * @type {Array}
   */
    this.courses = [];

    let googleApiLoaded = false;

    // Load the gapi google drive
    if (google.client && google.auth) {
      googleApiLoaded = true;
    }

    /**
   * Open a modal to show list of fetched class from google classroom
   * @returns {*|modal}
   */
    const openClassroomImportDialog = function () {
      const modal = BulbModalService.open({
        titleCode: '',
        template: '<bulb-google-classroom-import></bulb-google-classroom-import>',
        windowClass: 'bulb-onboarding-modal-with-close scrollable bulb-classroom-modal',
        closeOnClick: true,
        showOkButton: false,
        showCancelButton: false,
      });

      return modal;
    };

    /**
   * Handle response from google api during authorization process.
   * @param authResult result sent by google api.
   * @returns {*|promise}
   */
    const handleAuthResult = function (authResult) {
      const deferred = $q.defer();
      if (authResult && !authResult.error) {
        /* jshint camelcase: false */
        that.oauthToken = authResult.access_token;

        // Verify google connection exist with other account or not .
        Restangular.one('accounts/verifyGoogleConnection/GOOGLE').get({ code: authResult.code }).then(() => {
          deferred.resolve();
        }).catch((error) => {
          that.showError(error);
          deferred.reject(error);
        });
      } else {
        deferred.reject();
      }
      return deferred.promise;
    };

    /**
   *  Initializes the Google API client library and sets up sign-in state
   *  listeners.
   */
    function initClient() {
      const deferred = $q.defer();
      if (googleApiLoaded) {
        google.auth.setToken(null);
        that.oauthToken = null;
        google.auth.signOut();
        google.auth.authorize({
          client_id: CONFIG.google.client.id,
          scope: SCOPES,
          response_type: 'code token',
          discovery_docs: DISCOVERY_DOCS,
          prompt: 'select_account',
        }, (authResult) => {
          handleAuthResult(authResult).then(deferred.resolve);
        });
      }
      return deferred.promise;
    }

    /**
   * Method to filter courses that are already imported.
   * @param {Array} courses
   * @param {object} signedInGoogleUser
   */
    function filterCourses(courses, signedInGoogleUser) {
      for (var i = 0; i < courses.length; i++) {
        // Check if course has been already imported or not . if not imported then add to the list else
        // ignore it .
        const exists = BulbGroupService.groups.some((group) => // jshint ignore: line
          group.googleCourseId === courses[i].id);
        if (!exists) {
          courses[i].ownerId = signedInGoogleUser.id;
          courses[i].ownerEmail = signedInGoogleUser.email;
          that.courses.push(courses[i]);
        }
      }
    }

    /**
   * Fetched courses using classroom api from classroom.
   * @param signedInGoogleUser user who is connected with google.
   * @param hasNextToken token value
   * @returns {promise} promise of data.
   */
    function fetchCourses(signedInGoogleUser, hasNextToken) {
      const deferred = $q.defer();
      google.client.request({
        path: CLASSROOM.COURSES_URL + hasNextToken,
      }).then((response) => {
        const { courses } = response.result;
        if (courses) {
          filterCourses(courses, signedInGoogleUser);
        }
        deferred.resolve(response.result.nextPageToken);
      }, (error) => {
        BulbErrorService.showError('Cannot fetch courses from classroom', error);
        deferred.reject();
      });
      return deferred.promise;
    }

    /**
   * Fetch list of active courses from google classroom.
   * @returns {*|promise} list of courses.
   */
    const listCourses = function () {
      that.courses = [];
      let hasNextToken = '';
      let hasError = false;
      const deferred = $q.defer();
      let signedInGoogleUser;
      google.client.request({
        path: CLASSROOM.USER_INFO_URL,
      }).then(async (googleUser) => {
        signedInGoogleUser = googleUser.result;
        do {
          try {
            hasNextToken = await fetchCourses(signedInGoogleUser, hasNextToken);
          } catch (err) {
            hasError = true;
          }
        } while (hasNextToken);

        deferred.resolve();
        if (!hasError) {
          if (that.courses.length > 0) {
            openClassroomImportDialog();
          } else {
            console.log('No courses found.');
            BulbModalService.showMessage('import_class_from_classroom_no_more_class');
          }
        }
      }, (error) => {
        let errorMessage = $translate.instant('import_class_from_classroom_error_import');
        if (error.result.error.code == 401) {
          errorMessage = $translate.instant('import_class_from_classroom_error_authentication_failed');
        }
        BulbErrorService.showError(errorMessage);
        deferred.reject();
      }).catch((err) => {
        deferred.reject();
      });

      return deferred.promise;
    };

    /**
   * Import class type group from google classroom .
   * This will be called from group creation page .
   */
    this.importGroupFromClassroom = function () {
      initClient().then((auth) => {
        BulbBusyService.showBusyDuring(
          listCourses(),
        );
      });
    };

    /**
   * Get token from from google classroom  to re import course members.
   */
    this.reSyncGroupWithClassroom = function (courseId) {
      const deferred = $q.defer();
      initClient().then(() => {
        google.client.request({
          path: CLASSROOM.COURSE_BY_ID_URL + courseId,
        }).then((response) => {
          // Check if resync course is still in active states or not .
          if (response.result.courseState === 'ACTIVE') {
            deferred.resolve();
          } else {
            BulbModalService.showMessage('import_class_from_classroom_inactive_class');
            deferred.reject();
          }
        }, (error) => {
          BulbErrorService.error(`Cannot fetch courses from classroom with this courseId${courseId}`, error);
          deferred.reject();
        });
      });
      return deferred.promise;
    };

    /**
   * Show error when exception occur during courses fetch.
   * @param error object of error.
   */
    this.showError = function (error) {
      if (error.status === 409 && error.data && error.data.developerMessage) {
        // If we have detected an account conflict, show the information of the conflicting account:
        const parts = error.data.developerMessage.split(',');
        const params = {
          email: `<strong>${parts[0]}</strong>`,
        };
        const url = `${CONFIG.base.url}/${parts[1]}`;
        BulbErrorService.showError(`${$translate.instant('account_already_exists_error', params)
        }<a href=\"${url}\" target=\"_blank\">${url}</a>`, error);
      } else if (error.status === 409 && error.data && !error.data.developerMessage) {
        // If we have detected a group conflict
        BulbErrorService.showError($translate.instant('import_class_from_classroom_error_conflict'), error);
      } else {
        BulbErrorService.showError($translate.instant('import_class_from_classroom_error_import'), error);
      }
    };
  }
}());
